/**
 * PageSEO.jsx
 * Drop-in per-route <head> tag manager using react-helmet-async.
 *
 * Usage:
 *   import PageSEO from '@/components/PageSEO';
 *   <PageSEO
 *     title="Pharmacy Technician Diploma | Anderson College"
 *     description="..."
 *     canonical="https://andersoncollege.com/programs/pharmacy-technician"
 *     og={{ image: '/assets/programs/batch2/hero-pharmacy.jpg' }}
 *     jsonLd={...}
 *   />
 */

import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';

const BASE_URL = 'https://andersoncollege.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/assets/brand/hero-graduate.jpg`;
const SITE_NAME = 'Anderson College of Health, Business and Technology';

// ── Prerender ready-signal (2026-02 — fixes NO-TITLE on blog + language) ──
// Puppeteer waits for `window.__PRERENDER_READY === true` before taking
// the HTML snapshot (see scripts/prerender.js). This hook sets the flag
// as soon as PageSEO renders with a non-empty title AND Helmet has
// written it into <head>. A microtask + rAF guarantees Helmet's write
// has committed before we flip the flag — if we fired synchronously,
// Puppeteer could capture the DOM in a state where <title> is set but
// the matching <meta>/<link rel="canonical"> tags haven't been written
// yet.
//
// On blog detail pages that fetch CMS data asynchronously, PageSEO is
// NOT rendered until the fetch resolves — so this effect only runs
// after data lands, which is exactly the signal we want.
function usePrerenderReady(title) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!title || typeof title !== 'string' || title.trim().length === 0) return;
    // Two rAFs so Helmet has fully committed its <head> mutations.
    const r1 = window.requestAnimationFrame(() => {
      const r2 = window.requestAnimationFrame(() => {
        try {
          const docTitle = (document.title || '').trim();
          if (docTitle.length === 0) return;
          window.__PRERENDER_READY = true;
          // Also set a timestamp for debug / telemetry.
          window.__PRERENDER_READY_AT = Date.now();
        } catch { /* ignore */ }
      });
      // Cannot cancel the inner rAF through the outer handle, but these
      // fire ≤32ms later — fine.
      return r2;
    });
    return () => { try { window.cancelAnimationFrame(r1); } catch { /* ignore */ } };
  }, [title]);
}

export default function PageSEO({
  title,
  description,
  canonical,
  og = {},
  twitter = {},
  jsonLd = null,
  noIndex = false,
}) {
  const resolvedCanonical = canonical?.startsWith('http')
    ? canonical
    : `${BASE_URL}${canonical || '/'}`;

  const ogImage = og.image?.startsWith('http')
    ? og.image
    : `${BASE_URL}${og.image || DEFAULT_OG_IMAGE}`;

  // Signal the prerender script that this page is fully ready for
  // snapshot. Only fires once per route, keyed by title.
  usePrerenderReady(title);

  return (
    <Helmet>
      {/* ── Core ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={resolvedCanonical} />

      {/* ── Robots (inherits site-wide noindex during staging; flip in index.html when live) ── */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* ── Open Graph ── */}
      <meta property="og:type" content={og.type || 'website'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={og.title || title} />
      <meta property="og:description" content={og.description || description} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={og.imageAlt || 'Anderson College'} />
      <meta property="og:locale" content="en_CA" />

      {/* ── Twitter Card ── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitter.title || og.title || title} />
      <meta name="twitter:description" content={twitter.description || og.description || description} />
      <meta name="twitter:image" content={ogImage} />

      {/* ── JSON-LD ── */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
